import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChildren,
  inject,
  input,
  model,
  NO_ERRORS_SCHEMA,
  output,
} from '@angular/core';
import { Router } from '@angular/router';
import { faClose, faPlus } from '@fortawesome/free-solid-svg-icons';
import { tv, type VariantProps } from 'tailwind-variants';

import { BadgeComponent } from '../badge/badge';
import { IconComponent } from '../icon/icon';
import { TabComponent } from '../tab/tab';

export const tabListVariants = tv({
  base: 'tabs border-none p-0',
  compoundVariants: [
    { class: 'w-fit', fullWidth: false, variant: 'secondary' },
    { class: 'w-full', fullWidth: true, variant: 'secondary' },
  ],
  defaultVariants: {
    fullWidth: false,
    variant: 'default',
  },
  variants: {
    fullWidth: {
      false: '',
      true: '',
    },
    variant: {
      default: 'tabs-box gap-x-2',
      lift: 'tabs-lift gap-x-1',
      secondary: 'bg-base-300 gap-x-0.5 rounded-xl p-0.5',
    },
  },
});

export const tabItemVariants = tv({
  base: 'tab gap-x-0.5 overflow-hidden',
  compoundVariants: [
    { class: 'my-4', dense: false, variant: 'default' },
    { active: true, class: 'tab-active', variant: 'default' },
    {
      active: true,
      class: 'bg-i-surface-glass-raised-opaque text-base-content font-semibold',
      variant: 'lift',
    },
    {
      active: false,
      class: 'bg-base-300 text-i-text-helper font-normal',
      variant: 'lift',
    },
    {
      active: true,
      class: 'bg-base-100 text-base-content font-semibold',
      variant: 'secondary',
    },
    { active: false, class: 'text-i-text-helper', variant: 'secondary' },
    { class: 'min-w-12', fullWidth: false, variant: 'secondary' },
  ],
  defaultVariants: {
    active: false,
    dense: false,
    fullWidth: false,
    variant: 'default',
  },
  variants: {
    active: {
      false: '',
      true: '',
    },
    dense: {
      false: '',
      true: '',
    },
    fullWidth: {
      false: '',
      true: 'flex-1',
    },
    variant: {
      default: 'h-7',
      lift: 'h-7',
      secondary: 'h-6 rounded-lg text-xs',
    },
  },
});

export type TabsVariants = VariantProps<typeof tabListVariants>;

@Component({
  selector: 'app-tabs',
  imports: [CommonModule, BadgeComponent, IconComponent],
  templateUrl: './tabs.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [NO_ERRORS_SCHEMA],
})
export class TabsComponent {
  readonly #router = inject(Router);

  faClose = faClose;
  faPlus = faPlus;
  readonly hasNewTabButton = input<boolean>(false);
  readonly dense = input<boolean>(false);
  readonly variant = input<TabsVariants['variant']>('default');
  readonly fullWidth = input<boolean>(false);

  readonly closeTab = output<number>();
  readonly newTab = output<void>();

  readonly activeTab = model<number>(0);
  readonly tabListClasses = computed(() =>
    tabListVariants({
      fullWidth: this.fullWidth(),
      variant: this.variant(),
    })
  );

  readonly tabs = contentChildren<TabComponent>(TabComponent);

  protected tabClasses(active: boolean): string {
    return tabItemVariants({
      active,
      dense: this.dense(),
      fullWidth: this.fullWidth(),
      variant: this.variant(),
    });
  }

  protected onTabClick(index: number): void {
    this.activeTab.set(index);
    if (this.tabs()[index].path()) {
      this.#router.navigateByUrl(this.tabs()[index].path());
    }
  }

  protected onTabKeydown(event: KeyboardEvent, index: number): void {
    if (event.key === 'Enter' || event.key === ' ') {
      this.onTabClick(index);
      event.preventDefault();
    }
  }

  protected onCloseTab(event: MouseEvent, index: number): void {
    event.stopPropagation();
    this.closeTab.emit(index);
  }

  protected onCloseTabKeydown(event: KeyboardEvent, index: number): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.stopPropagation();
      event.preventDefault();
      this.closeTab.emit(index);
    }
  }

  protected onNewTab(event: MouseEvent): void {
    event.preventDefault();
    this.newTab.emit();
  }

  protected onNewTabKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.newTab.emit();
    }
  }
}
